import * as Sentry from '@sentry/react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import AuthenticationGuard from '~/containers/auth/AuthenticationGuard';
import LoginPage from '~/containers/auth/LoginPage';
import LogoutPage from '~/containers/auth/LogoutPage';
import AccessDeniedPage from '~/containers/errors/AccessDeniedPage';
import RouteErrorBoundary from '~/containers/errors/RouteErrorBoundary';
import PatientListRoute from '~/containers/patient-list';
import AppConfig from '~/utils/appconfig';
import DemoRoute from './demo-route';
import ViewerLayout from './layout';
import {
    LOCAL_VIEWER_PATH,
    PATIENT_LIST_PATH,
    PATIENT_VIEWER_PATH,
    SESSION_VIEWER_PATH,
} from './paths';
import ProtectedRoute from './protected-route';
import RequestStepIfUnnamed from './RequestStepIfUnnamed';
import SessionLayout from './SessionLayout';
import Viewer from './viewer';

/**
 * Landing route. A connector deployment opens on the patient list; a
 * standalone one opens on the local viewer, where a DICOM drop lands. Read at
 * render time, once AppConfig is initialized.
 */
function DefaultRoute() {
    return (
        <Navigate
            to={
                AppConfig.isConnectorMode
                    ? PATIENT_LIST_PATH
                    : LOCAL_VIEWER_PATH
            }
            replace
        />
    );
}

const sentryCreateBrowserRouter =
    Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([
    {
        path: '/login',
        element: <LoginPage />,
        errorElement: <RouteErrorBoundary />,
    },
    {
        path: '/logout',
        element: <LogoutPage />,
        errorElement: <RouteErrorBoundary />,
    },
    {
        path: '/',
        element: (
            <AuthenticationGuard>
                <ProtectedRoute>
                    <DemoRoute>
                        <ViewerLayout />
                    </DemoRoute>
                </ProtectedRoute>
            </AuthenticationGuard>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
            {
                path: '/',
                element: <DefaultRoute />,
            },
            {
                path: SESSION_VIEWER_PATH,
                element: <SessionLayout />,
                errorElement: <RouteErrorBoundary />,
                children: [
                    {
                        index: true,
                        // The TPS launches `/session/:id` with no step, and a
                        // session exists to have its contours reviewed.
                        element: (
                            <RequestStepIfUnnamed step="contouring">
                                <Viewer />
                            </RequestStepIfUnnamed>
                        ),
                    },
                ],
            },
            {
                path: PATIENT_VIEWER_PATH,
                element: <Viewer />,
                errorElement: <RouteErrorBoundary />,
            },
            {
                path: LOCAL_VIEWER_PATH,
                element: <Viewer />,
                errorElement: <RouteErrorBoundary />,
            },
        ],
    },

    {
        path: PATIENT_LIST_PATH,
        element: (
            <AuthenticationGuard>
                <ProtectedRoute>
                    <PatientListRoute />
                </ProtectedRoute>
            </AuthenticationGuard>
        ),
        errorElement: <RouteErrorBoundary />,
    },

    {
        path: '/access-denied',
        element: <AccessDeniedPage />,
    },
]);

export default router;
